import { ListItemText } from '@material-ui/core';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { Chip } from '../Chip';
import { VisuallyHidden } from '../VisuallyHidden';
import {
  BaseSelect,
  BaseSelectOption,
  BaseSelectProps,
  SelectValue,
  Value,
} from './BaseSelect';

const MultiSelectChip = styled(Chip)`
  margin-right: 0.5rem;
`;

const StyledSelectionIcon = styled(CheckBoxOutlineBlankIcon)`
  box-sizing: content-box;
  margin-right: 0.5rem;
`;

export type MultiSelectOption = BaseSelectOption & {
  label: string;
  /**
   * to highlight preexisting invalid values as invalid (e.g. invalid options added via the admin panel for things)
   * that are included in the options so the user can remove them
   */
  isInvalid?: boolean;
};

type MultiSelectProps = Omit<
  BaseSelectProps<MultiSelectOption>,
  'multiple' | 'renderOption' | 'renderValue'
>;

function MultiValueDisplay({
  value,
  options,
}: {
  value: SelectValue[];
  options: MultiSelectOption[];
}) {
  const chips = useMemo(() => {
    const selectedValues = Array.isArray(value) ? value : [];

    return selectedValues.map((selected) => {
      const opt = options.find((o) => o.value === selected);
      if (!opt || opt.isInvalid)
        return <MultiSelectChip color="danger" label={selected} />;
      return (
        <MultiSelectChip
          key={opt.value.toString()}
          color="tertiary"
          label={opt.label}
        />
      );
    });
  }, [options, value]);

  return <Value>{chips}</Value>;
}

function MultiSelectOptionDisplay({
  option,
  isSelected,
}: {
  option: MultiSelectOption;
  isSelected: boolean;
}) {
  const IconComponent = isSelected ? CheckBoxIcon : CheckBoxOutlineBlankIcon;
  const label = `${option.isInvalid ? 'Invalid: ' : ''}${option.label}`;

  return (
    <>
      <StyledSelectionIcon as={IconComponent} aria-hidden />
      <VisuallyHidden>{isSelected ? 'Selected' : 'Unselected'}</VisuallyHidden>

      {option.subText ? (
        <ListItemText primary={label} secondary={option.subText} />
      ) : (
        label
      )}
    </>
  );
}

export function MultiSelect(props: MultiSelectProps) {
  const { value, options } = props;

  const valueArray = useMemo(
    () => (Array.isArray(value) ? value : []) ?? [],
    [value],
  );

  const renderValue = useCallback(
    (val) => <MultiValueDisplay value={val} options={options} />,
    [options],
  );

  const renderOption = useCallback(
    (option: MultiSelectOption) => {
      const isSelected = valueArray.includes(option.value);
      return (
        <MultiSelectOptionDisplay option={option} isSelected={isSelected} />
      );
    },
    [valueArray],
  );

  return (
    <BaseSelect
      {...props}
      multiple
      isEmpty={valueArray.length === 0}
      renderValue={renderValue}
      renderOption={renderOption}
    />
  );
}
